import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFirestore, doc, getDoc, collection, query, where, getDocs, orderBy, limit, onSnapshot, Timestamp } from 'firebase/firestore';
import { StaggeredLoadingWrapper } from './ui/withLoading';
import { SkeletonPage, SkeletonStatCard, SkeletonCard, SkeletonGrid } from './ui/skeletons';
// Removed animation imports to prevent unwanted slide effects on page load
import { checkSubscriptionSilently } from '../lib/utils';
import { debugLog, debugWarn, errorLog } from '../utils/debug';
import styles from './Homepage.module.css';

export default function Homepage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkInCount, setCheckInCount] = useState(0);
  const [thisWeekDaysCount, setThisWeekDaysCount] = useState(0);
  const [thisWeekUnsubscribe, setThisWeekUnsubscribe] = useState(null);
  const [recentChats, setRecentChats] = useState([]);
  const [mostRecentChat, setMostRecentChat] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedMood, setSelectedMood] = useState(null);

  // Emotion data for the drawer
  const emotionData = {
    great: { emoji: '😊', title: "You're feeling great" },
    good: { emoji: '🙂', title: "You're feeling good" },
    okay: { emoji: '😐', title: "You're feeling okay" },
    sad: { emoji: '😔', title: "You're feeling sad" },
    anxious: { emoji: '😰', title: "You're feeling anxious" },
    tired: { emoji: '😴', title: "You're feeling tired" }
  };

  useEffect(() => {
    if (currentUser) {
      loadUserData();
      setupThisWeekQuery();
    }
    
    // Cleanup function
    return () => {
      if (thisWeekUnsubscribe) {
        thisWeekUnsubscribe();
      }
    };
  }, [currentUser]);

  // Handle background scroll when drawer is open/closed - Preserve native scrolling
  useEffect(() => {
    if (drawerOpen) {
      // Prevent background scroll while preserving native physics
      document.body.style.overflow = 'hidden';
      // Remove aggressive positioning that conflicts with native scrolling
    } else {
      // Re-enable natural scroll
      document.body.style.overflow = '';
    }

    // Cleanup function to restore natural scrolling on component unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  // Helper function to safely extract date string from chat data
  const getSafeDateString = (dateValue) => {
    if (!dateValue) return null;
    
    try {
      let date;
      
      // Handle Firestore Timestamp objects
      if (dateValue && typeof dateValue === 'object' && dateValue.toDate) {
        // This is a Firestore Timestamp
        date = dateValue.toDate();
        debugLog('🔥 Converting Firestore Timestamp to Date:', dateValue, '→', date);
      } else {
        // This is a regular date value
        date = new Date(dateValue);
      }
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        debugWarn('⚠️ Invalid date value:', dateValue);
        return null;
      }
      
      return date.toISOString().split('T')[0]; // YYYY-MM-DD
    } catch (error) {
      debugWarn('⚠️ Error processing date:', dateValue, error);
      return null;
    }
  };

  // Real-time ThisWeekQuery with live updates
  const setupThisWeekQuery = () => {
    try {
      const db = getFirestore();
      
      // Calculate start of current week (Monday 00:00:00)
      const startOfWeek = new Date();
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Monday
      startOfWeek.setDate(diff);
      startOfWeek.setHours(0, 0, 0, 0);
      
      debugLog('🔥 Setting up ThisWeekQuery with real-time listener...');
      debugLog('📅 Start of week:', startOfWeek.toISOString());
      debugLog('👤 User ID:', currentUser.uid);
      
      // Create the query: users/{auth.uid}/chats
      const userChatsRef = collection(db, 'users', currentUser.uid, 'chats');
      
      // First, let's try a simple query without any filters to see what data exists
      const simpleQuery = query(
        userChatsRef,
        limit(10)
      );
      
      debugLog('🔍 Simple query created to inspect data structure');
      
      // Set up real-time listener for simple query first
      const unsubscribe = onSnapshot(simpleQuery, 
        (snapshot) => {
          debugLog(`📊 Simple query snapshot received: ${snapshot.size} total docs`);
          
          // Debug: Log all documents to see what we're getting
          snapshot.docs.forEach((doc, index) => {
            const chatData = doc.data();
            debugLog(`📄 Doc ${index + 1}:`, {
              id: doc.id,
              endedAt: chatData.endedAt,
              ended_at: chatData.ended_at,
              timestamp: chatData.timestamp,
              createdAt: chatData.createdAt,
              created_at: chatData.created_at,
              status: chatData.status,
              // Log all available fields
              allFields: Object.keys(chatData)
            });
            
            // Additional debugging for date fields
            if (chatData.endedAt) {
              debugLog(`  📅 endedAt type:`, typeof chatData.endedAt, chatData.endedAt);
              if (typeof chatData.endedAt === 'object' && chatData.endedAt.toDate) {
                debugLog(`  🔥 endedAt is Firestore Timestamp, converted:`, chatData.endedAt.toDate());
              }
            }
            if (chatData.ended_at) {
              debugLog(`  📅 ended_at type:`, typeof chatData.ended_at, chatData.ended_at);
              if (typeof chatData.ended_at === 'object' && chatData.ended_at.toDate) {
                debugLog(`  🔥 ended_at is Firestore Timestamp, converted:`, chatData.endedAt.toDate());
              }
            }
            if (chatData.timestamp) {
              debugLog(`  📅 timestamp type:`, typeof chatData.timestamp, chatData.timestamp);
              if (typeof chatData.timestamp === 'object' && chatData.timestamp.toDate) {
                debugLog(`  🔥 timestamp is Firestore Timestamp, converted:`, chatData.timestamp.toDate());
              }
            }
          });
          
          // Now try to count unique days from this week
          const uniqueDays = new Set();
          snapshot.docs.forEach(doc => {
            const chatData = doc.data();
            // Try multiple possible date fields
            const dateValue = chatData.endedAt || chatData.ended_at || chatData.timestamp || chatData.createdAt || chatData.created_at;
            const dayString = getSafeDateString(dateValue);
            if (dayString) {
              // Check if this date is within the current week
              let chatDate;
              if (dateValue && typeof dateValue === 'object' && dateValue.toDate) {
                // This is a Firestore Timestamp
                chatDate = dateValue.toDate();
              } else {
                // This is a regular date value
                chatDate = new Date(dateValue);
              }
              
              debugLog(`🔍 Comparing chat date: ${chatDate.toISOString()} with start of week: ${startOfWeek.toISOString()}`);
              debugLog(`🔍 Is chat date >= start of week? ${chatDate >= startOfWeek}`);
              
              if (chatDate >= startOfWeek) {
                uniqueDays.add(dayString);
                debugLog(`✅ Added ${dayString} to unique days`);
              } else {
                debugLog(`❌ Skipped ${dayString} - not in current week`);
              }
            }
          });
          
          const count = uniqueDays.size;
          debugLog(`📊 ThisWeekQuery real-time update: ${count} unique days`);
          debugLog(`📅 Unique days found:`, Array.from(uniqueDays));
          setThisWeekDaysCount(count);
        },
        (error) => {
          errorLog('❌ Simple query listener error:', error);
          // If simple query fails, try fallback
          setupFallbackQuery();
        }
      );
      
      setThisWeekUnsubscribe(() => unsubscribe);
      
    } catch (error) {
      errorLog('❌ Error setting up ThisWeekQuery:', error);
      setupFallbackQuery();
    }
  };

  // Fallback query without compound index (if needed)
  const setupFallbackQuery = () => {
    try {
      const db = getFirestore();
      const startOfWeek = new Date();
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Monday
      startOfWeek.setDate(diff);
      startOfWeek.setHours(0, 0, 0, 0);
      
      debugLog('🔄 Setting up fallback query...');
      debugLog('📅 Start of week (fallback):', startOfWeek.toISOString());
      
      const userChatsRef = collection(db, 'users', currentUser.uid, 'chats');
      // Try a simple query without any filters to see what exists
      const fallbackQuery = query(
        userChatsRef,
        limit(20)
      );
      
      debugLog('🔍 Fallback query created:', fallbackQuery);
      
      const unsubscribe = onSnapshot(fallbackQuery, (snapshot) => {
        debugLog(`📊 Fallback query snapshot received: ${snapshot.size} total docs`);
        
        // Debug: Log all documents to see what we're getting
        snapshot.docs.forEach((doc, index) => {
          const chatData = doc.data();
          debugLog(`📄 Fallback Doc ${index + 1}:`, {
            id: doc.id,
            endedAt: chatData.endedAt,
            ended_at: chatData.ended_at,
            timestamp: chatData.timestamp,
            createdAt: chatData.createdAt,
            created_at: chatData.created_at,
            status: chatData.status,
            // Log all available fields
            allFields: Object.keys(chatData)
          });
          
          // Additional debugging for date fields
          if (chatData.endedAt) {
            debugLog(`  📅 endedAt type:`, typeof chatData.endedAt, chatData.endedAt);
            if (typeof chatData.endedAt === 'object' && chatData.endedAt.toDate) {
              debugLog(`  🔥 endedAt is Firestore Timestamp, converted:`, chatData.endedAt.toDate());
            }
          }
          if (chatData.ended_at) {
            debugLog(`  📅 ended_at type:`, typeof chatData.ended_at, chatData.ended_at);
            if (typeof chatData.ended_at === 'object' && chatData.ended_at.toDate) {
              debugLog(`  🔥 ended_at is Firestore Timestamp, converted:`, chatData.ended_at.toDate());
            }
          }
          if (chatData.timestamp) {
            debugLog(`  📅 timestamp type:`, typeof chatData.timestamp, chatData.timestamp);
            if (typeof chatData.timestamp === 'object' && chatData.timestamp.toDate) {
              debugLog(`  🔥 timestamp is Firestore Timestamp, converted:`, chatData.timestamp.toDate());
            }
          }
        });
        
        // Filter for status != "failed" on client side and count unique days
        const filteredDocs = snapshot.docs.filter(doc => {
          const status = doc.data().status;
          return status === 'pending' || status === 'ready';
        });
        
        debugLog(`🔍 Filtered docs after status filter: ${filteredDocs.length}`);
        
        // Count unique days instead of total chats
        const uniqueDays = new Set();
        filteredDocs.forEach(doc => {
          const chatData = doc.data();
          // Try multiple possible date fields
          const dateValue = chatData.endedAt || chatData.ended_at || chatData.timestamp || chatData.createdAt || chatData.created_at;
          const dayString = getSafeDateString(dateValue);
          if (dayString) {
            // Check if this date is within the current week
            let chatDate;
            if (dateValue && typeof dateValue === 'object' && dateValue.toDate) {
              // This is a Firestore Timestamp
              chatDate = dateValue.toDate();
            } else {
              // This is a regular date value
              chatDate = new Date(dateValue);
            }
            
            if (chatDate >= startOfWeek) {
              uniqueDays.add(dayString);
              debugLog(`✅ Added ${dayString} to unique days (fallback)`);
            } else {
              debugLog(`❌ Skipped ${dayString} - not in current week (fallback)`);
            }
          }
        });
        
        const count = uniqueDays.size;
        debugLog(`📊 Fallback query real-time update: ${count} unique days`);
        debugLog(`📅 Unique days found (fallback):`, Array.from(uniqueDays));
        setThisWeekDaysCount(count);
      });
      
      setThisWeekUnsubscribe(() => unsubscribe);
      
    } catch (error) {
      errorLog('❌ Fallback query also failed:', error);
      setThisWeekDaysCount(0);
    }
  };

  useEffect(() => {
    // Update greeting based on time of day
    updateGreeting();
  }, [userData]);

  // Get dynamic text based on ThisWeekQuery count
  const getThisWeekText = () => {
    if (thisWeekDaysCount === 0) {
      return "That's okay - whenever you're ready, Venty's here.";
    } else if (thisWeekDaysCount === 1) {
      return "That's one time you've paused to check in - it matters.";
    } else if (thisWeekDaysCount === 2) {
      return "Two moments you've given yourself already - that's progress.";
    } else if (thisWeekDaysCount === 3) {
      return "You're starting to build consistency - that's how clarity grows.";
    } else if (thisWeekDaysCount === 4) {
      return "Showing up this often helps you notice patterns before they pile up.";
    } else if (thisWeekDaysCount === 5) {
      return "Regular reflection like this makes it easier to stay balanced.";
    } else if (thisWeekDaysCount === 6) {
      return "Staying this consistent strengthens your ability to handle whatever comes up.";
    } else if (thisWeekDaysCount === 7) {
      return "Daily reflection builds resilience - you're giving yourself that gift.";
    }
    return "That's okay - whenever you're ready, Venty's here.";
  };

  const loadUserData = async () => {
    try {
      // First, silently check subscription status
      debugLog('🔍 Silently checking subscription status...');
      const isSubscriptionValid = await checkSubscriptionSilently(currentUser, navigate);
      
      if (!isSubscriptionValid) {
        return; // Navigation will be handled by the utility function
      }
      
      // Subscription is valid, continue loading user data
      debugLog('✅ Subscription valid, loading user data...');
      
      const db = getFirestore();
      
      // F) Dashboard "this week" counter - read from subcollection
      const startOfWeek = new Date();
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Monday
      startOfWeek.setDate(diff);
      startOfWeek.setHours(0, 0, 0, 0);
      
      debugLog('🔥 Loading user data with subcollection count...');
      
      // Load user document data first
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);
        debugLog('📄 User document loaded:', data);
      } else {
        debugWarn('⚠️ User document does not exist');
      }
      
      try {
        // Get check-in count from subcollection
        const userChatsRef = collection(db, 'users', currentUser.uid, 'chats');
        const countQuery = query(
          userChatsRef,
          where('endedAt', '>=', startOfWeek)
        );
        
        const countSnapshot = await getDocs(countQuery);
        // Count unique days instead of total chats
        const uniqueDays = new Set();
        countSnapshot.docs.forEach(doc => {
          const chatData = doc.data();
          const dayString = getSafeDateString(chatData.endedAt);
          if (dayString) {
            uniqueDays.add(dayString);
          }
        });
        const checkInCount = uniqueDays.size;
        
        debugLog(`📊 Check-in count from subcollection: ${checkInCount} unique days`);
        setCheckInCount(checkInCount);
        
        // Get recent chats for display
        const recentQuery = query(
          userChatsRef,
          orderBy('endedAt', 'desc'),
          limit(4)
        );
        
        const recentSnapshot = await getDocs(recentQuery);
        const recentChats = [];
        let mostRecent = null;
        
        recentSnapshot.forEach((doc, index) => {
          const chatData = doc.data();
          const chat = {
            id: doc.id,
            timestamp: chatData.endedAt,
            endedAt: chatData.endedAt,
            summary: chatData.summary,
            status: chatData.status
          };
          
          // Set mostRecent to the first 'ready' chat (for "Pick up where you left off" section)
          if (chatData.status === 'ready' && !mostRecent) {
            mostRecent = chat;
          }
          
          if (chatData.status === 'ready') recentChats.push(chat);
        });
        
        setRecentChats(recentChats);
        setMostRecentChat(mostRecent);
        
        debugLog(`💬 Recent chats from subcollection: ${recentChats.length}`);
        
      } catch (subcollectionError) {
        errorLog('❌ Error loading from subcollection, falling back to user doc:', subcollectionError);
        
        // Fallback to legacy arrays method
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          
          // Calculate check-in count and process chat data using legacy method
          const { count, chats, mostRecent } = processUserChats(data);
          setCheckInCount(count);
          setRecentChats(chats);
          setMostRecentChat(mostRecent);
          
          debugLog(`📄 Fallback - Check-in count: ${count}, Recent chats: ${chats.length}`);
        }
      }
      
    } catch (err) {
      errorLog('Error loading user data:', err);
      setError('Error loading user data');
    } finally {
      setLoading(false);
    }
  };

  const processUserChats = (userData) => {
    let allChats = [];
    
    // Collect chats from chatSummaries
    if (userData.chatSummaries && Array.isArray(userData.chatSummaries)) {
      allChats = [...allChats, ...userData.chatSummaries.filter(chat => chat.status === 'ready')];
    }
    
    // Collect chats from pastConversations
    if (userData.pastConversations && Array.isArray(userData.pastConversations)) {
      const summaryConversations = userData.pastConversations
        .filter(conv => conv.type === 'summary' && conv.status === 'ready')
        .map(conv => ({
          id: conv.chatId,
          timestamp: conv.timestamp,
          ended_at: conv.timestamp,
          summary: conv.summary,
          started_at: conv.started_at || null
        }));
      allChats = [...allChats, ...summaryConversations];
    }
    
    // Sort by timestamp (most recent first)
    allChats.sort((a, b) => {
      const dateA = new Date(a.timestamp || a.ended_at || 0);
      const dateB = new Date(b.timestamp || b.ended_at || 0);
      
      // Check if dates are valid before comparison
      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
        debugWarn('⚠️ Invalid date in sort comparison:', a.timestamp || a.ended_at, b.timestamp || b.ended_at);
        return 0; // Keep original order if dates are invalid
      }
      
      return dateB - dateA;
    });
    
    // Calculate check-in count (unique days in current week)
    const checkInCount = calculateCheckInCount(allChats);
    
    // Get recent chats (first 4 for display)
    const recentChats = allChats.slice(0, 4);
    
    // Get most recent chat
    const mostRecentChat = allChats.length > 0 ? allChats[0] : null;
    
    return {
      count: checkInCount,
      chats: recentChats,
      mostRecent: mostRecentChat
    };
  };

  const calculateCheckInCount = (chats) => {
    if (!chats.length) return 0;
    
    const now = new Date();
    const startOfWeek = new Date();
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Monday
    startOfWeek.setDate(diff);
    startOfWeek.setHours(0, 0, 0, 0);
    
    // Get unique days (YYYY-MM-DD format) for chats in current week
    const uniqueDays = new Set();
    
    chats.forEach(chat => {
      let chatDate;
      const dateValue = chat.timestamp || chat.ended_at;
      
      // Handle Firestore Timestamp objects
      if (dateValue && typeof dateValue === 'object' && dateValue.toDate) {
        chatDate = dateValue.toDate();
      } else {
        chatDate = new Date(dateValue);
      }
      
      // Check if the date is valid before processing
      if (isNaN(chatDate.getTime())) {
        debugWarn('⚠️ Invalid date in chat data:', dateValue);
        return; // Skip this chat
      }
      
      // Only count chats from current week
      if (chatDate >= startOfWeek && chatDate <= now) {
        const dayString = chatDate.toISOString().split('T')[0]; // YYYY-MM-DD
        uniqueDays.add(dayString);
      }
    });
    
    debugLog(`📅 Unique check-in days in current week:`, Array.from(uniqueDays));
    return uniqueDays.size;
  };

  const updateGreeting = () => {
    const hour = new Date().getHours();
    let greeting = 'Welcome back';
    
    if (hour < 12) {
      greeting = 'Good morning';
    } else if (hour < 17) {
      greeting = 'Good afternoon';
    } else {
      greeting = 'Welcome back';
    }
    
    return greeting;
  };

  const formatDate = () => {
    const now = new Date();
    return now.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'unknown';
    
    try {
      const now = new Date();
      let date;
      
      // Handle Firestore Timestamp objects
      if (dateString && typeof dateString === 'object' && dateString.toDate) {
        date = dateString.toDate();
      } else {
        date = new Date(dateString);
      }
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        debugWarn('⚠️ Invalid date in formatRelativeTime:', dateString);
        return 'unknown';
      }
      
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'today';
      if (diffDays === 1) return '1d';
      if (diffDays < 7) return `${diffDays}d`;
      if (diffDays < 14) return '1w';
      if (diffDays < 30) return `${Math.floor(diffDays / 7)}w`;
      return `${Math.floor(diffDays / 30)}m`;
    } catch (error) {
      debugWarn('⚠️ Error formatting relative time:', dateString, error);
      return 'unknown';
    }
  };

  const getUserName = () => {
    // Primary: pull from the user's firebase doc from the field "name"
    if (userData?.name) return userData.name;
    
    // Secondary: email username
    if (currentUser?.email) {
      const name = currentUser.email.split('@')[0];
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
    return 'there';
  };

  const handleStartChat = () => {
    navigate('/chat');
  };

  const handleSeeAllChats = () => {
    navigate('/chats');
  };

  const handleChatClick = (chatId) => {
    navigate(`/sessionsummary/${chatId}`);
  };

  const handleMoodSelection = (mood, event) => {
    // Prevent accidental triggering during scroll
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    debugLog(`Mood selected: ${mood}`);
    setSelectedMood(mood);
    
    // Small delay to ensure drawer renders before animating
    setTimeout(() => {
      setDrawerOpen(true);
      document.body.style.overflow = 'hidden';
    }, 10);
  };

  const closeDrawer = () => {
    // First animate the drawer closed
    setDrawerOpen(false);
    
    // Wait for the slide-down animation to complete before removing from DOM
    setTimeout(() => {
      setSelectedMood(null);
      document.body.style.overflow = '';
    }, 400); // Match the CSS transition duration
  };

  const handleStartConversation = () => {
    closeDrawer();
    setTimeout(() => {
      navigate('/chat');
    }, 300);
  };

  if (loading) {
    return (
      <div className={styles.homepageContainer}>
        <div className={styles.appShell}>
          <div className={styles.content}>
            {/* Settings Icon Skeleton */}
            <div 
              className="absolute top-4 right-4 w-8 h-8 rounded-full"
              style={{ backgroundColor: 'rgba(18, 51, 47, 0.1)' }}
            />
            
            {/* Header Section Skeleton */}
            <div className={styles.headerSection}>
              <div className="h-8 rounded w-1/3 mb-2" style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }} />
              <div className="h-5 rounded w-1/4 mb-5" style={{ backgroundColor: 'rgba(142, 142, 147, 0.3)' }} />
              
              {/* Stats Card Skeleton */}
              <div 
                className="rounded-2xl p-6 flex items-center justify-between"
                style={{ 
                  background: 'linear-gradient(135deg, rgba(242, 249, 243, 0.8) 0%, rgba(232, 245, 234, 0.8) 100%)',
                  border: '1px solid rgba(127, 176, 105, 0.2)'
                }}
              >
                <div className="space-y-3 flex-1">
                  <div className="h-6 rounded w-3/4" style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }} />
                  <div className="h-4 rounded w-1/2" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
                </div>
                <div className="w-12 h-12 rounded-full" style={{ backgroundColor: 'rgba(127, 176, 105, 0.2)' }} />
              </div>
            </div>

            {/* Stats Section Skeleton */}
            <div className="stats-section grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {Array.from({ length: 3 }).map((_, index) => (
                <div 
                  key={index}
                  className="rounded-2xl p-6 text-center"
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(242, 249, 243, 0.8) 0%, rgba(232, 245, 234, 0.8) 100%)',
                    border: '1px solid rgba(127, 176, 105, 0.2)'
                  }}
                >
                  <div className="w-12 h-12 rounded-full mx-auto mb-3" style={{ backgroundColor: 'rgba(127, 176, 105, 0.2)' }} />
                  <div className="h-6 rounded w-1/2 mx-auto mb-2" style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }} />
                  <div className="h-8 rounded w-1/3 mx-auto mb-2" style={{ backgroundColor: 'rgba(127, 176, 105, 0.2)' }} />
                  <div className="h-4 rounded w-2/3 mx-auto" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
                </div>
              ))}
            </div>

            {/* Recent Chats Section Skeleton */}
            <div className="recent-chats-section">
              <div className="h-7 rounded w-1/3 mb-6" style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }} />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div 
                    key={index}
                    className="rounded-2xl p-6"
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(242, 249, 243, 0.8) 0%, rgba(232, 245, 234, 0.8) 100%)',
                      border: '1px solid rgba(127, 176, 105, 0.2)'
                    }}
                  >
                    <div className="space-y-3">
                      <div className="h-5 rounded w-1/2" style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }} />
                      <div className="h-4 rounded w-full" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
                      <div className="h-4 rounded w-2/3" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
                      <div className="flex space-x-2">
                        <div className="h-6 rounded w-16" style={{ backgroundColor: 'rgba(18, 51, 47, 0.1)' }} />
                        <div className="h-6 rounded w-20" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.homepageContainer}>
        <div className={styles.appShell}>
          <div className={styles.content}>
            <div className={styles.errorState}>
              <div className={styles.errorIcon}>⚠️</div>
              <div className={styles.errorTitle}>Error Loading Dashboard</div>
              <div className={styles.errorSubtitle}>{error}</div>
              <button className={styles.retryBtn} onClick={loadUserData}>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.homepageContainer}>
      <div className={styles.appShell}>
        <div className={styles.content}>
          {/* Settings Icon */}
          <div 
            className={`${styles.settingsIcon} ${!loading ? styles.contentAnimated : ''}`}
            onClick={() => navigate('/settings')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" 
                 width="31" height="31" 
                 fill="#12332F" 
                 viewBox="0 0 24 24">
              <path d="M19.14 12.936c.036-.303.06-.612.06-.936s-.024-.633-.072-.936l2.037-1.59a.51.51 0 0 0 
              .12-.648l-1.928-3.342a.506.506 0 0 0-.615-.21l-2.397.96a6.99 6.99 0 0 0-1.62-.936l-.36-2.55a.5.5 0 0 0-.498-.426h-3.856a.5.5 
              0 0 0-.498.426l-.36 2.55a6.99 6.99 0 0 0-1.62.936l-2.397-.96a.506.506 0 0 0-.615.21L2.712 8.526a.51.51 0 0 0 
              .12.648l2.037 1.59c-.048.303-.072.612-.072.936s.024.633.072.936l-2.037 1.59a.51.51 0 0 0-.12.648l1.928 
              3.342c.144.24.42.33.615.21l2.397-.96c.498.39 1.05.702 1.62.936l.36 2.55c.06.24.258.426.498.426h3.856c.24 0 
              .438-.186.498-.426l.36-2.55c.57-.234 1.122-.546 1.62-.936l2.397.96c.21.12.486.03.615-.21l1.928-3.342a.51.51 
              0 0 0-.12-.648l-2.037-1.59ZM12 15.6A3.6 3.6 0 1 1 15.6 12 3.6 3.6 0 0 1 12 15.6Z"/>
            </svg>
          </div>

          {/* Header Section */}
          <div className={`${styles.headerSection} ${!loading ? styles.contentAnimated : ''}`}>
            <div className={styles.greeting}>{updateGreeting()}, {getUserName()}</div>
            <div className={styles.dateTime}>{formatDate()}</div>

            <div className={styles.statsCard}>
              <div className={styles.statsContent}>
                <div className={styles.statsMainText}>
                  {thisWeekDaysCount === 0 ? (
                    "You haven't checked in yet this week."
                  ) : thisWeekDaysCount === 1 ? (
                    <>You've checked in <span className={styles.statsHighlight}>1 day</span> this week.</>
                  ) : (
                    <>You've checked in <span className={styles.statsHighlight}>{thisWeekDaysCount} days</span> this week.</>
                  )}
                </div>
                <div className="stats-sub-text">
                  {getThisWeekText()}
                </div>
              </div>
            </div>
          </div>

          {/* Mood Check-in */}
          <div className={`${styles.moodSection} ${!loading ? styles.contentAnimatedDelayed : ''}`}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>How are you feeling?</div>
            </div>

            <div className={styles.moodGrid}>
              <div className={`${styles.moodCard} interactive`} onClick={(e) => handleMoodSelection('great', e)}>
                <span className={styles.moodEmoji}>😊</span>
                <div className={styles.moodLabel}>Great</div>
              </div>
              <div className={`${styles.moodCard} interactive`} onClick={(e) => handleMoodSelection('good', e)}>
                <span className={styles.moodEmoji}>🙂</span>
                <div className={styles.moodLabel}>Good</div>
              </div>
              <div className={`${styles.moodCard} interactive`} onClick={(e) => handleMoodSelection('okay', e)}>
                <span className={styles.moodEmoji}>😐</span>
                <div className={styles.moodLabel}>Okay</div>
              </div>
              <div className={`${styles.moodCard} interactive`} onClick={(e) => handleMoodSelection('sad', e)}>
                <span className={styles.moodEmoji}>😔</span>
                <div className={styles.moodLabel}>Sad</div>
              </div>
              <div className={`${styles.moodCard} interactive`} onClick={(e) => handleMoodSelection('anxious', e)}>
                <span className={styles.moodEmoji}>😰</span>
                <div className={styles.moodLabel}>Anxious</div>
              </div>
              <div className={`${styles.moodCard} interactive`} onClick={(e) => handleMoodSelection('tired', e)}>
                <span className={styles.moodEmoji}>😴</span>
                <div className={styles.moodLabel}>Tired</div>
              </div>
            </div>
          </div>

          {/* Action */}
          <div className={`actions-section ${!loading ? styles.contentAnimatedDelayed : ''}`} style={{ marginBottom: '32px' }}>
            <button className={`${styles.primaryAction} interactive`} onClick={handleStartChat}>
              💬 Start a new conversation
            </button>
          </div>

          {/* Cards */}
          <div className={`cards-section ${!loading ? styles.contentAnimatedDelayedMore : ''}`}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>Pick up where you left off...</div>
            </div>
            {mostRecentChat ? (
              <div 
                className={`${styles.contentCard} interactive`}
                onClick={() => handleChatClick(mostRecentChat.id)}
              >
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitle}>
                    {mostRecentChat.summary?.title || 'Recent session'}
                  </div>
                  <div className={styles.cardIcon}>🔄</div>
                </div>
                <div className={styles.cardContent}>
                  {mostRecentChat.summary?.public_summary || 'Your most recent chat session is ready to review.'}
                </div>
                <div className={styles.cardMeta}>
                  {formatRelativeTime(mostRecentChat.timestamp || mostRecentChat.ended_at)}
                </div>
              </div>
            ) : (
              <div className={styles.contentCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitle}>Ready to get started?</div>
                  <div className={styles.cardIcon}>💭</div>
                </div>
                <div className={styles.cardContent}>
                  Start your first conversation with Venty to begin your mental health journey.
                </div>
                <div className={styles.cardMeta}>No previous sessions</div>
              </div>
            )}
          </div>

          {/* Chat history */}
          <div className={`chat-history ${!loading ? styles.contentAnimatedDelayedMore : ''}`}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>Recent conversations</div>
              <button className={styles.seeAllBtn} onClick={handleSeeAllChats}>
                See all
              </button>
            </div>

            {recentChats.length > 0 ? (
              recentChats.map((chat, index) => {
                const emojis = ['🧠', '😴', '🎯', '💕'];
                return (
                  <div 
                    key={chat.id || index} 
                    className={`${styles.chatItem} interactive`}
                    onClick={() => handleChatClick(chat.id)}
                  >
                    <div className={styles.chatAvatar}>{emojis[index] || '💬'}</div>
                    <div className={styles.chatContent}>
                      <div className={styles.chatTitle}>
                        {chat.summary?.title || 'Chat Session'}
                      </div>
                      <div className={styles.chatPreview}>
                        {chat.summary?.public_summary || 'Conversation summary'}
                      </div>
                    </div>
                    <div className={styles.chatTime}>
                      {formatRelativeTime(chat.timestamp || chat.ended_at)}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={styles.chatItem}>
                <div className={styles.chatAvatar}>💭</div>
                <div className={styles.chatContent}>
                  <div className={styles.chatTitle}>No conversations yet</div>
                  <div className={styles.chatPreview}>Start your first chat to see it here</div>
                </div>
                <div className={styles.chatTime}>-</div>
              </div>
            )}
          </div>

          {/* Overlay + Emotion Drawer - Only render when mood is selected */}
          {selectedMood && (
            <>
              <div className={`${styles.drawerOverlay} ${drawerOpen ? styles.drawerOverlayActive : ''}`} onClick={closeDrawer}></div>
              <div className={`${styles.emotionDrawer} ${drawerOpen ? styles.emotionDrawerActive : ''}`}>
                <div className={styles.drawerHandle}></div>
                <div className={styles.drawerContent}>
                  <div className={styles.drawerEmotionDisplay}>
                    {emotionData[selectedMood]?.emoji}
                  </div>
                  <div className={styles.drawerTitle}>
                    {emotionData[selectedMood]?.title}
                  </div>
                  <div className={styles.drawerSubtitle}>Want to talk about it?</div>
                  <button className={styles.drawerActionBtn} onClick={handleStartConversation}>
                    Start Conversation
                  </button>
                  <button className={styles.drawerSecondaryBtn} onClick={closeDrawer}>
                    Maybe Later
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}